'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { Mail, Phone, ExternalLink } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Button } from '@/components/ui/button';
import CourseCard from '@/components/ui/CourseCard';
import NumberedPagination from '@/components/ui/NumberedPagination';
import { serverDate } from '@/lib/time/serverClock';

const PAGE_SIZE = 12;
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface OrganizerData {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  description: string | null;
  image_url: string | null;
  website_url: string | null;
  facebook_url: string | null;
}

type TabType = 'upcoming' | 'past';

export default function OrganizerPage() {
  const params = useParams();
  const router = useRouter();
  const { t, language } = useLanguage();
  const supabase = createClient();
  const eventsRef = useRef<HTMLDivElement>(null);

  const [organizer, setOrganizer] = useState<OrganizerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [activeTab, setActiveTab] = useState<TabType>('upcoming');
  const [upcomingCount, setUpcomingCount] = useState(0);
  const [pastCount, setPastCount] = useState(0);

  const [events, setEvents] = useState<any[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Cache page 1 of each tab to avoid re-fetching on tab switch
  const [upcomingPage1, setUpcomingPage1] = useState<any[] | null>(null);
  const [pastPage1, setPastPage1] = useState<any[] | null>(null);

  const identifier = params.identifier as string;

  const fetchOrganizer = useCallback(async () => {
    const isUUID = UUID_REGEX.test(identifier);
    let query = supabase
      .from('organizer_profiles')
      .select('id, name, email, phone, description, image_url, website_url, facebook_url');

    query = isUUID ? query.eq('id', identifier) : query.eq('slug', identifier);

    const { data, error } = await query.limit(1).maybeSingle();

    if (error || !data) {
      setNotFound(true);
      setLoading(false);
      return null;
    }

    setOrganizer(data);
    return data;
  }, [identifier, supabase]);

  const buildEventQuery = useCallback(
    (organizerId: string, tab: TabType) => {
      const now = serverDate().toISOString();
      let query = supabase
        .from('content')
        .select(
          'id, title, type, status, image_url, start_date, end_date, timezone, created_at, cancelled_at, event_format, organizer_id, is_private, tickets(id, name, price, currency, status, max_quantity, sold_quantity, requires_promocode)'
        )
        .eq('organizer_id', organizerId)
        .eq('status', 'published')
        .not('is_private', 'is', true)
        .is('cancelled_at', null);

      if (tab === 'upcoming') {
        query = query.or(`end_date.is.null,end_date.gte.${now}`);
        query = query.order('start_date', { ascending: true });
      } else {
        query = query.lt('end_date', now);
        query = query.order('start_date', { ascending: false });
      }

      return query;
    },
    [supabase]
  );

  const fetchCounts = useCallback(
    async (organizerId: string) => {
      const now = serverDate().toISOString();
      const baseFilters = (q: any) =>
        q
          .eq('organizer_id', organizerId)
          .eq('status', 'published')
          .not('is_private', 'is', true)
          .is('cancelled_at', null);

      const [upcomingRes, pastRes] = await Promise.all([
        baseFilters(
          supabase
            .from('content')
            .select('id', { count: 'exact', head: true })
        ).or(`end_date.is.null,end_date.gte.${now}`),
        baseFilters(
          supabase
            .from('content')
            .select('id', { count: 'exact', head: true })
        ).lt('end_date', now),
      ]);

      setUpcomingCount(upcomingRes.count ?? 0);
      setPastCount(pastRes.count ?? 0);
    },
    [supabase]
  );

  const fetchEvents = useCallback(
    async (organizerId: string, tab: TabType, page: number) => {
      setEventsLoading(true);
      const from = (page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      const { data, error } = await buildEventQuery(organizerId, tab).range(from, to);

      if (!error && data) {
        setEvents(data);
        // Cache page 1
        if (page === 1) {
          if (tab === 'upcoming') setUpcomingPage1(data);
          else setPastPage1(data);
        }
      }
      setEventsLoading(false);
    },
    [buildEventQuery]
  );

  // Initial load
  useEffect(() => {
    (async () => {
      const org = await fetchOrganizer();
      if (!org) return;
      await Promise.all([fetchCounts(org.id), fetchEvents(org.id, 'upcoming', 1)]);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identifier]);

  const handleTabChange = (tab: TabType) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    setCurrentPage(1);

    // Use cached page 1 if available
    const cached = tab === 'upcoming' ? upcomingPage1 : pastPage1;
    if (cached) {
      setEvents(cached);
    } else if (organizer) {
      fetchEvents(organizer.id, tab, 1);
    }
  };

  const handlePageChange = (page: number) => {
    if (!organizer) return;
    const totalPages = Math.ceil(
      (activeTab === 'upcoming' ? upcomingCount : pastCount) / PAGE_SIZE
    );
    const clamped = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(clamped);
    fetchEvents(organizer.id, activeTab, clamped);

    // Scroll to events section
    eventsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  // Not found state
  if (notFound || !organizer) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-2">{t('organizerPage.organizerNotFound')}</p>
          <p className="text-sm text-gray-400 mb-4">
            {t('organizerPage.organizerNotFoundDescription')}
          </p>
          <Button variant="link" onClick={() => router.push('/')} className="p-0">
            {t('courseDetails.returnToHomepage')}
          </Button>
        </div>
      </div>
    );
  }

  const activeCount = activeTab === 'upcoming' ? upcomingCount : pastCount;
  const totalPages = Math.ceil(activeCount / PAGE_SIZE);
  const initials = organizer.name
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* Organizer Profile Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col sm:flex-row gap-5">
            {/* Logo / Initials */}
            {organizer.image_url ? (
              <img
                src={organizer.image_url}
                alt={organizer.name}
                className="w-[72px] h-[72px] rounded-xl object-cover border border-gray-200 flex-shrink-0"
              />
            ) : (
              <div className="w-[72px] h-[72px] rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0">
                <span className="text-xl font-bold text-blue-600">{initials}</span>
              </div>
            )}

            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-bold text-gray-900 mb-1">{organizer.name}</h1>
              {organizer.description && (
                <div className="text-sm text-gray-600 mb-3 leading-relaxed [&_a]:text-blue-600 [&_a]:underline [&_a:hover]:text-blue-700 [&_strong]:font-semibold [&_p]:mb-1 [&_p:last-child]:mb-0">
                  <ReactMarkdown>{organizer.description}</ReactMarkdown>
                </div>
              )}

              {/* Contact details */}
              <div className="flex flex-wrap gap-x-5 gap-y-1.5">
                {organizer.email && (
                  <a
                    href={`mailto:${organizer.email}`}
                    className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-blue-600"
                  >
                    <Mail className="h-3.5 w-3.5" />
                    {organizer.email}
                  </a>
                )}
                {organizer.phone && (
                  <a
                    href={`tel:${organizer.phone}`}
                    className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-blue-600"
                  >
                    <Phone className="h-3.5 w-3.5" />
                    {organizer.phone}
                  </a>
                )}
                {organizer.website_url && (
                  <a
                    href={organizer.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-blue-600"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    {organizer.website_url.replace(/^https?:\/\//, '')}
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs + Events Section */}
        <div ref={eventsRef} className="scroll-mt-[80px]">
          {/* Tabs */}
          <div className="flex gap-1 border-b border-gray-200 mb-6">
            <button
              onClick={() => handleTabChange('upcoming')}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'upcoming'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('organizerPage.upcoming')} ({upcomingCount})
            </button>
            <button
              onClick={() => handleTabChange('past')}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'past'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('organizerPage.past')} ({pastCount})
            </button>
          </div>

          {/* Events Grid */}
          {eventsLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">
                {activeTab === 'upcoming'
                  ? t('organizerPage.noUpcomingEvents')
                  : t('organizerPage.noPastEvents')}
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {events.map((event) => (
                  <CourseCard key={event.id} course={event} variant="discovery" />
                ))}
              </div>

              <NumberedPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
